import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal — fades/slides its children in the first time they enter the viewport.
 * Robust by design: content is never left permanently hidden.
 *   1. If IntersectionObserver is unavailable, show immediately.
 *   2. If the element is already within the viewport on mount, show immediately
 *      (covers above-the-fold content and very tall viewports).
 *   3. Otherwise observe, with a fallback timer that reveals anyway if the
 *      observer never fires.
 * Respects prefers-reduced-motion via CSS (.reveal rules in index.css).
 */
export default function Reveal({ as: Tag = "div", delay = 0, className = "", children, ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return;

    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (el.getBoundingClientRect().top < vh * 0.95) {
      setShown(true);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    obs.observe(el);

    // Safety net: never leave content hidden if the observer never fires.
    const fallback = setTimeout(() => setShown(true), 2500);

    return () => {
      obs.disconnect();
      clearTimeout(fallback);
    };
  }, [shown]);

  const delayClass = delay ? ` d${delay}` : "";
  return (
    <Tag
      ref={ref}
      className={`reveal${delayClass}${shown ? " in-view" : ""} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}
